import { ReadinessModel, RouteClass, } from "../contracts/common.js";
import {} from "../contracts/result.js";
import { failContract } from "../internal/errors.js";
import { expectBoolean, expectNumber, expectObject, expectOptionalString, expectString, parseLivePage, readField, readOptionalField, } from "../internal/json.js";
import { encodePathSegment, createQuery } from "../internal/query.js";
import { requestJsonResult } from "../internal/transport.js";
const PROPOSALS_ROUTE = {
    method: "GET",
    name: "proposals",
    path: "/api/v1/proposals",
    readinessModel: ReadinessModel.LIVE,
    routeClass: RouteClass.LIVE,
};
const PROPOSAL_ROUTE = {
    method: "GET",
    name: "proposal",
    path: "/api/v1/proposal/{hash}",
    readinessModel: ReadinessModel.LIVE,
    routeClass: RouteClass.LIVE,
};
const PROPOSAL_VOTES_ROUTE = {
    method: "GET",
    name: "proposalVotes",
    path: "/api/v1/proposal/{hash}/votes",
    readinessModel: ReadinessModel.LIVE,
    routeClass: RouteClass.LIVE,
};
const SEARCH_ROUTE = {
    method: "GET",
    name: "search",
    path: "/api/v1/search",
    readinessModel: ReadinessModel.NONE,
    routeClass: RouteClass.CROSS_DOMAIN,
};
/**
 * Public route descriptors for governance and search routes.
 */
export const governanceSearchRouteDescriptors = {
    proposal: PROPOSAL_ROUTE,
    proposalVotes: PROPOSAL_VOTES_ROUTE,
    proposals: PROPOSALS_ROUTE,
    search: SEARCH_ROUTE,
};
function parseProposalDetail(value, path) {
    const object = expectObject(value, path);
    const collateralHash = expectOptionalString(readOptionalField(object, "collateralHash"), `${path}.collateralHash`);
    const url = expectOptionalString(readOptionalField(object, "url"), `${path}.url`);
    const paymentAddress = expectOptionalString(readOptionalField(object, "paymentAddress"), `${path}.paymentAddress`);
    const startTime = expectOptionalString(readOptionalField(object, "startTime"), `${path}.startTime`);
    const endTime = expectOptionalString(readOptionalField(object, "endTime"), `${path}.endTime`);
    const creationTime = expectOptionalString(readOptionalField(object, "creationTime"), `${path}.creationTime`);
    return {
        hash: expectString(readField(object, "hash", path), `${path}.hash`),
        ...(collateralHash == null ? {} : { collateralHash }),
        name: expectString(readField(object, "name", path), `${path}.name`),
        ...(url == null ? {} : { url }),
        ...(paymentAddress == null ? {} : { paymentAddress }),
        paymentAmount: expectNumber(readField(object, "paymentAmount", path), `${path}.paymentAmount`),
        ...(startTime == null ? {} : { startTime }),
        ...(endTime == null ? {} : { endTime }),
        votes: parseProposalVoteSummary(readField(object, "votes", path), `${path}.votes`),
        fundingThreshold: expectNumber(readField(object, "fundingThreshold", path), `${path}.fundingThreshold`),
        meetsFundingThreshold: expectBoolean(readField(object, "meetsFundingThreshold", path), `${path}.meetsFundingThreshold`),
        funded: expectBoolean(readField(object, "funded", path), `${path}.funded`),
        valid: expectBoolean(readField(object, "valid", path), `${path}.valid`),
        ...(creationTime == null ? {} : { creationTime }),
    };
}
function parseProposalVoteSummary(value, path) {
    const object = expectObject(value, path);
    return {
        yes: expectNumber(readField(object, "yes", path), `${path}.yes`),
        no: expectNumber(readField(object, "no", path), `${path}.no`),
        abstain: expectNumber(readField(object, "abstain", path), `${path}.abstain`),
    };
}
function parseProposalVote(value, path) {
    const object = expectObject(value, path);
    const voteTime = expectOptionalString(readOptionalField(object, "voteTime"), `${path}.voteTime`);
    return {
        proTxHash: expectString(readField(object, "proTxHash", path), `${path}.proTxHash`),
        vote: expectString(readField(object, "vote", path), `${path}.vote`),
        ...(voteTime == null ? {} : { voteTime }),
    };
}
/**
 * The wire field carrying each variant's identifier.
 *
 * The daemon names the identifier after what it *is* — `blockHash`, `txHash`,
 * `address`, `proTxHash`, `hash` — one key per variant, matching the sibling
 * block/transaction/validator routes. `SearchResult` normalizes all five to
 * `id`, because a search dropdown renders every kind through one code path and
 * has no use for five differently-named keys.
 *
 * This mapping used to be absent: every variant was read from `id`, a key the
 * API has never emitted. `readField` therefore threw on the first result and
 * `parseSearchList` failed as a whole, so a working search endpoint rendered an
 * empty dropdown. It survived because the fixtures were hand-written to match
 * the parser rather than the daemon.
 */
const SEARCH_ID_FIELD = {
    address: "address",
    block: "blockHash",
    proposal: "hash",
    tx: "txHash",
    validator: "proTxHash",
};
function readSearchId(object, kind, path) {
    const field = SEARCH_ID_FIELD[kind];
    return expectString(readField(object, field, path), `${path}.${field}`);
}
function parseSearchResult(value, path) {
    const object = expectObject(value, path);
    const kind = expectString(readField(object, "type", path), `${path}.type`);
    switch (kind) {
        case "address":
            return {
                type: "address",
                id: readSearchId(object, "address", path),
            };
        case "block": {
            const blockTags = expectObject(readField(object, "blockTags", path), `${path}.blockTags`);
            return {
                type: "block",
                id: readSearchId(object, "block", path),
                status: expectString(readField(object, "status", path), `${path}.status`),
                blockTags: {
                    orphan: expectBoolean(readField(blockTags, "orphan", `${path}.blockTags`), `${path}.blockTags.orphan`),
                },
            };
        }
        case "proposal":
            return {
                type: "proposal",
                id: readSearchId(object, "proposal", path),
            };
        case "tx": {
            const txTags = expectObject(readField(object, "txTags", path), `${path}.txTags`);
            return {
                type: "tx",
                id: readSearchId(object, "tx", path),
                txTags: {
                    mined: expectBoolean(readField(txTags, "mined", `${path}.txTags`), `${path}.txTags.mined`),
                    coinbase: expectBoolean(readField(txTags, "coinbase", `${path}.txTags`), `${path}.txTags.coinbase`),
                },
            };
        }
        case "validator":
            return {
                type: "validator",
                id: readSearchId(object, "validator", path),
                status: expectString(readField(object, "status", path), `${path}.status`),
            };
        default:
            return failContract(`${path}.type`, `unsupported search result type ${kind}`);
    }
}
function parseSearchList(value, path) {
    return {
        value: parseLivePage(value, path, parseSearchResult).value,
    };
}
function createCursorQuery(params) {
    if (params == null) {
        return undefined;
    }
    const query = createQuery({
        cursor: params.cursor,
        limit: params.limit,
    });
    return query.size > 0 ? query : undefined;
}
/**
 * Build the governance and search route family for one client transport context.
 */
export function buildGovernanceSearchRoutes(context) {
    return {
        async getProposal(hash) {
            return requestJsonResult(context, PROPOSAL_ROUTE, `/api/v1/proposal/${encodePathSegment(hash)}`, parseProposalDetail);
        },
        async getProposalVotes(hash, params) {
            const query = createCursorQuery(params);
            return requestJsonResult(context, PROPOSAL_VOTES_ROUTE, `/api/v1/proposal/${encodePathSegment(hash)}/votes`, (value, path) => parseLivePage(value, path, parseProposalVote), ...(query == null ? [] : [{ query }]));
        },
        async listProposals(params) {
            const query = createCursorQuery(params);
            return requestJsonResult(context, PROPOSALS_ROUTE, PROPOSALS_ROUTE.path, (value, path) => parseLivePage(value, path, parseProposalDetail), ...(query == null ? [] : [{ query }]));
        },
        async search(query) {
            const params = createQuery({ q: query });
            return requestJsonResult(context, SEARCH_ROUTE, SEARCH_ROUTE.path, parseSearchList, { query: params });
        },
    };
}
//# sourceMappingURL=governanceSearch.js.map